import { useEffect, useState } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useFramework } from '@/framework';
import { useLocaleStore } from '@/i18n';
import type { ReferenceLines } from '@/plugins/apps/user-editor/reference-lines';
import type { RulerBar } from '@/plugins/apps/user-editor/ruler-bar';
import { getSysType, SYSTEM_TYPE } from '@/utils/platform-kit';
import { useStageToolbarState } from '../../states/stage-toolbar-state';
import type { IStageToolbarItemRenderArgs } from '../../types';

interface IReferenceLinesItemProps {
    active: boolean;
    setActive(active: boolean): void;
}

const ReferenceLinesItem = ({ active, setActive }: IReferenceLinesItemProps) => {
    const { service } = useFramework();
    const { $t } = useLocaleStore();
    const visible = useStageToolbarState()((state) => state.referenceLineVisible);
    const [showRulerBar, setShowRulerBar] = useState(
        service.registration.getInstance<RulerBar>('RulerBar')?.getShowRulerBar(),
    );

    const rulerBar = service.registration.getInstance<RulerBar>('RulerBar');
    const referenceLines = service.registration.getInstance<ReferenceLines>('ReferenceLines');

    useEffect(() => {
        return rulerBar?.onShowRulerBarChanged((show) => {
            setShowRulerBar(show);
        });
    }, [rulerBar]);

    if (!visible || (!rulerBar && !referenceLines)) return null;

    const hotKey = getSysType() === SYSTEM_TYPE.MAC ? '⇧ + R' : 'Shift + R';

    return (
        <div className="stage-toolbar-reference-lines" onMouseDown={(event) => event.stopPropagation()}>
            <Tooltip
                placement="top"
                title={$t((value) => value.navigatorLayer.referenceLinesAndRulers)}
                trigger="hover"
            >
                <Popover
                    open={active}
                    onOpenChange={setActive}
                    content={
                        <ul>
                            {rulerBar ? (
                                <>
                                    <li
                                        onClick={() => {
                                            if (showRulerBar) rulerBar.hideRulerBar();
                                            else rulerBar.showRulerBar();
                                        }}
                                    >
                                        {showRulerBar
                                            ? $t((value) => value.navigatorLayer.hideRulerBar)
                                            : $t((value) => value.navigatorLayer.showRulerBar)}{' '}
                                        {hotKey}
                                    </li>
                                    <div className="divide-line" />
                                </>
                            ) : null}
                            {referenceLines ? (
                                <>
                                    <li onClick={() => referenceLines.addHorizontalReferenceLine()}>
                                        {$t((value) => value.navigatorLayer.horizontalReferenceLine)}
                                    </li>
                                    <li onClick={() => referenceLines.addVerticalReferenceLine()}>
                                        {$t((value) => value.navigatorLayer.verticalReferenceLine)}
                                    </li>
                                    <li onClick={() => referenceLines.clearReferenceLines()}>
                                        {$t((value) => value.navigatorLayer.clearReferenceLine)}
                                    </li>
                                </>
                            ) : null}
                        </ul>
                    }
                    trigger="click"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    placement="top"
                    overlayClassName="reference-line-operation-menu"
                >
                    <span>
                        <IconFont name="ReferenceLineBold" fontSize={16} />
                    </span>
                </Popover>
            </Tooltip>
        </div>
    );
};

export const referenceLinesItemRender = (args: IStageToolbarItemRenderArgs) => {
    const { config, toolbar, active, setActive } = args;
    if (!config.items.referenceLines || !toolbar.getItemAvailability('reference-lines')) return null;
    return <ReferenceLinesItem active={active} setActive={setActive} />;
};
